import { ReportTypeEnum } from '@va/dashboard/feature/general-settings';
import { Clock, NavWebStatsOverview, NotesIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import {
  fontWeights,
  IconWithBackground,
  ListCardItemWithIcon,
  Paragraph,
  paragraphSizes,
  SelectableButtonsGroup,
  ToggleActionCard,
} from '@va/ui/design-system';
import Skeleton from 'react-loading-skeleton';
import {
  NewsletterSettingsFormFields,
  NewsletterSettingsFormFieldsType,
  useNewsletterSettings,
} from './useNewsletterSettings';

export const NewsletterSettingsForm: React.FC<{
  formId: string;
  onSubmit: (values: NewsletterSettingsFormFieldsType) => void;
  initialValues?: NewsletterSettingsFormFieldsType;
}> = ({ onSubmit, formId, initialValues }) => {
  const {
    onSubscriptionChange,
    getFrequency,
    values,
    selectedFrequency,
    onReportsChange,
    isSubscriptionLoading,
    isReportsLoading,
  } = useNewsletterSettings({ initialFormValues: initialValues });
  const translate = useTranslate();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(values);
  };

  if (isSubscriptionLoading || isReportsLoading)
    return (
      <div className='w-full mx-auto px-2 flex flex-col gap-2'>
        <Skeleton className='h-72 rounded-18' />
        <Skeleton className='h-72 rounded-18' />
        <Skeleton className='h-72 rounded-18' />
      </div>
    );

  return (
    <form id={formId} onSubmit={handleSubmit} className='w-full mx-auto flex gap-3 flex-col'>
      <ToggleActionCard
        title={translate('form.emailReports.statisticsReports')}
        subtitle={translate('form.emailReports.reportsSubtitle')}
        name={NewsletterSettingsFormFields.reportType}
        subSectionClass='p-1.5!'
        value={values.reportType !== ReportTypeEnum.never}
        handleChange={({ value }) => {
          onReportsChange({
            name: NewsletterSettingsFormFields.reportType,
            value: value ? ReportTypeEnum.daily : ReportTypeEnum.never,
          });
        }}
        contentClassName='gap-9px'
        icon={() => (
          <IconWithBackground className='bg-white w-9! h-9!' icon={() => <NavWebStatsOverview color='#666' />} />
        )}
        wrapperClass='rounded-12! overflow-hidden'
        className='p-1.5! pr-3! rounded-12'
        heightClass='h-12! min-h-12!'
        titleClass='py-0 leading-5.25'
      >
        <div className={'flex w-full justify-between rounded-12 sm:flex-col gap-y-1.5 bg-white-snow flex-wrap'}>
          <div className='grow-10'>
            <ListCardItemWithIcon
              className='max-w-full!'
              icon={<IconWithBackground className='bg-white w-9! h-9!' icon={() => <Clock strokeColor='#666' />} />}
              topText={translate('unsubscribePage.frequencyReports')}
              topTextProps={{ className: 'leading-5.25' }}
              bottomNode={
                <Paragraph
                  size={paragraphSizes.tiny}
                  weight={fontWeights.medium}
                  colorClassName='text-primary'
                  className='!text-gray-devilSolid leading-15!'
                >
                  {translate('form.emailReports.frequencyReportsSubtitle')}
                </Paragraph>
              }
            />
          </div>
          <div className='overflow-x-auto scrollbar scrollbar-thumb flex items-center sm:w-full'>
            <SelectableButtonsGroup
              buttons={getFrequency()}
              selectedValue={selectedFrequency}
              onChange={(value) =>
                onReportsChange({ name: NewsletterSettingsFormFields.reportType, value: Number(value) })
              }
              className={'bg-white-smoke! justify-center! rounded-9px! sm:w-full'}
              activeButtonColor='secondary'
              inactiveButtonColor='tertiary'
            />
          </div>
        </div>
      </ToggleActionCard>
      <ToggleActionCard
        title={translate('onboarding.newsletterStep.newsletter')}
        name={NewsletterSettingsFormFields.newsletter}
        value={values.newsletter}
        handleChange={onSubscriptionChange}
        contentClassName='gap-9px'
        icon={() => <IconWithBackground className='bg-white w-9! h-9!' icon={() => <NotesIcon color='#696969' />} />}
        wrapperClass='rounded-12! overflow-hidden'
        className='p-1.5! pr-3! rounded-12'
        heightClass='h-12! min-h-12!'
        titleClass='py-0 leading-5.25'
      />
    </form>
  );
};
